import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import { AnimatePresence, motion } from "motion/react";
import {
  MDSButton,
  MDSButtonVariant,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
  MDSIconKind,
  MDSMenuContentList,
  MDSMenuContentItemKind,
  MDSIcon,
  MDSCircularProgress,
  MDSIconColor,
  MDSSidePanelHeader,
  MDSSidePanelHeaderSlotName,
} from "@/mds";
import { ZIndex } from "@/domains/design/constants";
import { SidePanelLoaderBar } from "./side-panel-loader-bar";
import { useHeadsUpContext } from "@/components/heads-up/context/heads-up-context";
import { HeadsUpViewState } from "@/components/heads-up/context/heads-up-context";
import { useHeadsUpAnalytics } from "@/components/heads-up/analytics";
import { type FC, useMemo, useCallback, useState, useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { HeadsUpFullEmptyView } from "./views/heads-up-full-empty-view";
import { HeadsUpGeneratingView } from "./views/heads-up-generating-view";
import { HeadsUpErrorView } from "./views/heads-up-error-view";
import { HeadsUpNoContentView } from "./views/heads-up-no-content-view";
import { HeadsUpContentView } from "./views/heads-up-content-view";
import { HeadsUpLockedView } from "./views/heads-up-locked-view";
import { HeadsUpFindMoreSection } from "./find-more";
import { UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { HeadsUpSidebarHeader } from "@/components/heads-up/sidebar/header/heads-up-sidebar-header";
import { SIDE_PANEL_COLLAPSED_BUFFER_PX } from "@/app/router/constants";
import { OnboardingTooltip } from "@/components/note/floating-actions/onboarding-tooltip";
import { useAppStore } from "@/store";
import { clientEnvModule } from "@/modules/client-env";
import { useHeadsUpUnlockThreshold } from "./hooks/use-heads-up-unlock-threshold";
import { pluralize } from "@/modules/pluralize";

export const HeadsUpSidebarContent: FC = observer(() => {
  const { context, content, activity, interactions, resources, ui } = useHeadsUpContext();
  const { generateMeetingBrief, refresh, regenerate, findMore, openHeadsUp } = interactions;
  const analytics = useHeadsUpAnalytics();
  const { store } = useAppStore();
  const theme = useTheme();

  const canRefresh = activity.canRefresh;
  const canGenerateBrief = activity.canGenerateBrief;
  const isGeneratingBrief = activity.isGeneratingBrief;
  const canRegenerate = activity.canRegenerate;
  const showLoaderBar = activity.isRefreshing;
  const isFindMoreDisabled = activity.isFindingMore || !activity.canFindMore;

  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [isCoachMarkOpen, setIsCoachMarkOpen] = useState(false);
  const headerTriggerRef = useRef<HTMLDivElement>(null);

  const handleHeaderClick = useCallback(() => {
    if (!ui.isChatOverlayOpen) return;
    openHeadsUp();
  }, [openHeadsUp, ui.isChatOverlayOpen]);

  const handleHeaderMouseEnter = useCallback(() => {
    if (!ui.isChatOverlayOpen) return;
    setIsHeaderHovered(true);
  }, [ui.isChatOverlayOpen]);

  const handleHeaderMouseLeave = useCallback(() => {
    setIsHeaderHovered(false);
  }, []);

  const animationKey = context.contextId ?? "heads-up";
  const pendingSnapshot = content.pendingSnapshot;
  const untrashedNoteCount = context.untrashedNoteCount ?? 0;
  const {
    threshold: unlockThreshold,
    notesRemaining: notesRemainingToUnlock,
    isLocked,
    // note that we MUST account for the untrashedNoteCount to be empty to avoid flashes
  } = useHeadsUpUnlockThreshold({ nullableUntrashedNoteCount: context.untrashedNoteCount });

  const shouldShowHeadsUpCoachMark =
    store.platformInfo.onboardingInfo.shouldShowHeadsUpCoachMark &&
    !clientEnvModule.isMobileBrowser();

  const handleTooltipOpenChange = useCallback(
    async (newOpenValue: boolean) => {
      // when switching the coach mark from on to off, dismiss it through the store
      // which dismisses it via the API
      if (isCoachMarkOpen && !newOpenValue && shouldShowHeadsUpCoachMark) {
        await store.platformInfo.dismissHeadsUpCoachMark();
      }
      setIsCoachMarkOpen(newOpenValue);
    },
    [isCoachMarkOpen, shouldShowHeadsUpCoachMark, store.platformInfo]
  );

  useEffect(() => {
    let isTooltipOpen = false;
    if (!ui.isChatOverlayOpen) {
      setIsHeaderHovered(false);
      if (
        !isLocked &&
        content.viewState === HeadsUpViewState.GeneratedHasContent &&
        shouldShowHeadsUpCoachMark
      ) {
        isTooltipOpen = true;
      }
    }
    handleTooltipOpenChange(isTooltipOpen);
  }, [
    ui.isChatOverlayOpen,
    isLocked,
    content.viewState,
    shouldShowHeadsUpCoachMark,
    handleTooltipOpenChange,
  ]);

  const titleIsLoading = Boolean(
    context.activeNoteId && resources.isNoteLoading(context.activeNoteId)
  );
  const headerTitle = (() => {
    if (titleIsLoading) {
      return "Loading note…";
    }

    if (!context.activeNoteId) {
      return "No note selected";
    }

    return context.activeNoteTitle ?? UNTITLED_NOTE_TITLE;
  })();

  const handleRegenerateClick = useCallback(() => {
    analytics.trackRegenerateClick();
    regenerate();
  }, [analytics, regenerate]);

  const moreMenuContent = useMemo<MDSMenuContentList>(() => {
    const items: MDSMenuContentList = [];

    if (canGenerateBrief) {
      items.push({
        id: "generate-meeting-briefing",
        kind: MDSMenuContentItemKind.Item,
        label: isGeneratingBrief ? "Generating meeting briefing..." : "Generate meeting briefing",
        icon: MDSIconKind.CalendarEvent,
        onClick: generateMeetingBrief,
        isDisabled: isGeneratingBrief,
      });
    }

    items.push({
      id: "regenerate",
      kind: MDSMenuContentItemKind.Item,
      label: "Regenerate from scratch",
      icon: MDSIconKind.Refresh,
      onClick: handleRegenerateClick,
      isDisabled: !canRegenerate,
    });

    return items;
  }, [
    canGenerateBrief,
    isGeneratingBrief,
    generateMeetingBrief,
    canRegenerate,
    handleRegenerateClick,
  ]);

  const renderCurrentView = useMemo(() => {
    switch (content.viewState) {
      case HeadsUpViewState.FullEmpty:
        return <HeadsUpFullEmptyView />;
      case HeadsUpViewState.Generating:
        return <HeadsUpGeneratingView />;
      case HeadsUpViewState.Error:
        return <HeadsUpErrorView message={content.error} onRetry={regenerate} />;
      case HeadsUpViewState.GeneratedNoContent:
        if (activity.isLiveActive) {
          return (
            <HeadsUpContentView
              cards={content.primaryCards}
              cardRenderOptions={resources.cardRenderOptions}
              isLiveActive={activity.isLiveActive}
            />
          );
        }
        return <HeadsUpNoContentView />;
      case HeadsUpViewState.GeneratedHasContent:
      default:
        return (
          <HeadsUpContentView
            cards={content.primaryCards}
            cardRenderOptions={resources.cardRenderOptions}
            isLiveActive={activity.isLiveActive}
          />
        );
    }
  }, [
    content.error,
    content.primaryCards,
    content.viewState,
    regenerate,
    resources.cardRenderOptions,
    activity.isLiveActive,
  ]);

  return (
    <Container $isHoveringHeader={isHeaderHovered && ui.isChatOverlayOpen}>
      <OnboardingTooltip
        title="See related notes at your fingertips"
        subtitle="Heads Up automatically surfaces related notes and connections as you work"
        triggerRef={headerTriggerRef}
        isOpen={isCoachMarkOpen && shouldShowHeadsUpCoachMark}
        onOpenChange={handleTooltipOpenChange}
        placement="left top"
        pointCorner="top-right"
        offset={-4}
        crossOffset={-21}
        width={188}
      />

      <MDSSidePanelHeader
        onClick={handleHeaderClick}
        isDisabled={!ui.isChatOverlayOpen}
        onMouseEnter={handleHeaderMouseEnter}
        onMouseLeave={handleHeaderMouseLeave}
        backgroundColor="transparent"
        bottomBorderColor={ui.isChatOverlayOpen ? "transparent" : theme.colors.border.onAccent}
      >
        <HeaderLeftContent slot={MDSSidePanelHeaderSlotName.Heading}>
          <MDSText
            variant={MDSTextVariant.TextS}
            weight={MDSTextWeight.Smedium}
            color={MDSTextColor.Contrast}
          >
            Heads Up
          </MDSText>
          {isLocked ? (
            <MDSIcon kind={MDSIconKind.Lock} size={14} color={MDSIconColor.Secondary} />
          ) : null}
        </HeaderLeftContent>
        {isLocked ? (
          <HeaderRightContent slot={MDSSidePanelHeaderSlotName.Actions}>
            <MDSText
              variant={MDSTextVariant.TextS}
              weight={MDSTextWeight.Medium}
              color={MDSTextColor.Secondary}
            >
              Create {notesRemainingToUnlock} {pluralize(notesRemainingToUnlock, "note", "notes")}{" "}
              to unlock
            </MDSText>

            <MDSCircularProgress current={untrashedNoteCount} total={unlockThreshold} animated />
          </HeaderRightContent>
        ) : null}
      </MDSSidePanelHeader>

      <SidePanelLoaderBar
        active={showLoaderBar}
        inset={24}
        isChatOverlayOpen={ui.isChatOverlayOpen}
      />

      {isLocked ? null : (
        <HeadsUpSidebarHeader
          viewState={content.viewState}
          formattedRelativeTime={content.formattedRelativeTime}
          headerTitle={headerTitle}
          onRefresh={refresh}
          canRefresh={canRefresh}
          moreMenuContent={moreMenuContent}
          activeNoteId={context.activeNoteId ?? null}
        />
      )}

      <Body>
        {isLocked ? (
          <HeadsUpLockedView untrashedNoteCount={untrashedNoteCount} />
        ) : (
          <>
            {pendingSnapshot ? (
              <PendingUpdatesContainer>
                <MDSButton variant={MDSButtonVariant.Emphasis} onClick={pendingSnapshot.accept}>
                  See new changes
                </MDSButton>
              </PendingUpdatesContainer>
            ) : null}
            <div
              ref={headerTriggerRef}
              style={{ width: "fit-content" }}
              data-test-id="heads-up-sidebar-feature-flyout-anchor"
            ></div>
            <AnimatePresence mode="wait">
              <AnimatedView
                key={animationKey}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {renderCurrentView}

                <HeadsUpFindMoreSection
                  viewState={content.viewState}
                  onFindMore={findMore}
                  isFindingMore={activity.isFindingMore}
                  isDisabled={isFindMoreDisabled}
                  hasFoundMore={activity.hasFoundMore}
                  findMoreCards={content.findMoreCards}
                  cardRenderOptions={resources.cardRenderOptions}
                />
              </AnimatedView>
            </AnimatePresence>
          </>
        )}
      </Body>
    </Container>
  );
});

const Container = styled.div<{ $isHoveringHeader: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme, $isHoveringHeader }) => {
    if ($isHoveringHeader) {
      return theme.colors.surface.headsUp.hover;
    }

    return theme.colors.surface.headsUp.default;
  }};
  transition: background-color ${({ theme }) => theme.transitions.background};
  user-select: none;
  border-top-right-radius: ${({ theme }) => theme.borderRadius.xlarge};
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-bottom: ${SIDE_PANEL_COLLAPSED_BUFFER_PX}px;
  position: relative;

  code {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  pre {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
`;

const AnimatedView = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const PendingUpdatesContainer = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing["4"]};
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
  z-index: ${ZIndex.Dropdown};

  & > * {
    pointer-events: auto;
  }
`;

const HeaderLeftContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const HeaderRightContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;
